import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { API_URL } from '../config/api';

// Create axios instance with base configuration
// Use relative URL in development to leverage Vite proxy, absolute URL in production
const getBaseURL = () => {
  // Check if we're in development mode and running on a different port
  const isDev = import.meta.env.DEV;
  const currentPort = window.location.port;
  const isDevServer = isDev && (currentPort === '3001' || currentPort === '3002' || currentPort !== '3000');

  if (isDevServer) {
    // Use relative URL to leverage Vite proxy
    return '/api';
  }

  // Use centralized API URL configuration
  return API_URL;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    const apiKey = import.meta.env.VITE_AUTH_API_KEY;

    // Add the static API key for backend authentication
    config.headers['X-API-Key'] = apiKey;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper to check if we should redirect to login
const shouldRedirectToLogin = (url?: string): boolean => {
  // Don't redirect for auth endpoints
  const authEndpoints = ['/auth/login', '/auth/register', '/auth/register-with-cv', '/auth/check-existing-user'];
  return !authEndpoints.some(endpoint => url?.includes(endpoint));
};

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig;

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Check if we should redirect to login
      if (shouldRedirectToLogin(originalRequest.url)) {
        // Clear authentication data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');

        // Redirect to login page
        window.location.href = '/login';

        // Return a rejected promise with auth error
        return Promise.reject({
          ...error,
          message: 'Authentication required. Please log in.',
          requiresAuth: true
        });
      }
    }

    // Format error message
    const responseData = error.response?.data as { detail?: string; message?: string } | undefined;
    const message = responseData?.detail ||
                   responseData?.message ||
                   error.message ||
                   'An unexpected error occurred';

    return Promise.reject({ ...error, message });
  }
);

// Helper function for multipart/form-data requests
export const uploadFile = async (url: string, formData: FormData) => {
  return apiClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export default apiClient;